import {MDCTabBar} from '@material/tab-bar';
import { Observable, Subject, fromEvent, from } from 'rxjs';
import { debounceTime, map, switchMap, catchError } from 'rxjs/operators';
import toastr from 'toastr';
import axios from 'axios';
import { formatTimeDifference } from './app';
import { TokenStorageService } from './tokenStorage.js';

const tokenStorageService = new TokenStorageService();

class MangaChapterManager {
    private chapters = [];
    private activeSource: string | null = null;
    private mangaSlug: string | undefined = undefined;
    private chaptersLoaded: boolean = false;
    private chaptersLoading: boolean = false;
    private searchSubject = new Subject<string>();

    // DOM Elements
    private tabBar: MDCTabBar;
    private tabs: NodeListOf<HTMLButtonElement>;
    private searchInput: HTMLInputElement;
    private chapterTemplate: HTMLTemplateElement;
    private chapterList: HTMLUListElement;
    private loadingElement: HTMLDivElement;
    private chaptersNotLoaded: HTMLDivElement;
    private chapterDisplayResults: HTMLDivElement;
    private noChaptersElement: HTMLParagraphElement;

    constructor() {
        // Initialize DOM elements
        this.tabBar = new MDCTabBar(document.querySelector('.mdc-tab-bar'));
        this.tabs = document.querySelectorAll('.mdc-tab');
        this.searchInput = document.getElementById('search_chapters') as HTMLInputElement;
        this.chapterTemplate = document.querySelector('.full_chapters_list .chapter-lister-display template') as HTMLTemplateElement;
        this.chapterList = document.querySelector('.full_chapters_list .chapter-lister-display') as HTMLUListElement;
        this.loadingElement = document.querySelector('.full_chapters_list .loading-search-results') as HTMLDivElement;
        this.chaptersNotLoaded = document.querySelector('.full_chapters_list .chapters_not_loaded') as HTMLDivElement;
        this.chapterDisplayResults = document.querySelector('.full_chapters_list .chapter-display-loaded-results') as HTMLDivElement;
        this.noChaptersElement = document.querySelector('.full_chapters_list .no-chapters') as HTMLParagraphElement;

        this.initializeEventListeners();
        this.initializeSearchSubscription();

        // Initialize UI state
        const sourceId = this.tabs[0].dataset.source!;
        this.mangaSlug = this.tabs[0].dataset.slug;
        this.changeSource(sourceId);
    }

    private initializeEventListeners(): void {
        // Source tab click listeners using RxJS
        this.tabs.forEach(tab => {
            fromEvent(tab, 'click').subscribe((e: Event) => {
                const sourceId = (e.currentTarget as HTMLButtonElement).dataset.source;
                if (sourceId) {
                    this.changeSource(sourceId);
                }
            });
        });

        // Load chapters button listener
        const loadButton = document.getElementById('loadChapters');
        if (loadButton) {
            fromEvent(loadButton, 'click')
                .pipe(
                    switchMap(() => this.loadChapters())
                )
                .subscribe();
        }
    }

    private initializeSearchSubscription(): void {
        // Setup search with debounce
        if (this.searchInput) {
            fromEvent(this.searchInput, 'input')
                .pipe(
                    map((e: Event) => (e.target as HTMLInputElement).value),
                    debounceTime(300)
                )
                .subscribe(searchTerm => {
                    this.filterChapters(searchTerm);
                });
        }
    }

    private loadChapters(): Observable<[]> {
        if (!this.activeSource || this.chaptersLoading) {
            return new Observable(subscriber => subscriber.complete());
        }

        this.chaptersLoaded = true;
        this.chaptersLoading = true;
        this.updateUIState('loading');

        return from(axios.get<[]>(`https://api.abyssreader.com/api/chapters/manga/${this.activeSource}`))
            .pipe(
                map(response => response.data),
                catchError(error => {
                    this.chaptersLoaded = false;
                    this.updateUIState('error');
                    this.showToast('An error occurred during the process, please try again!', 'error');
                    throw error;
                }),
                map(chapters => {
                    this.chapters = chapters;
                    this.renderChapters(this.chapters);
                    this.updateUIState('loaded');
                    this.chaptersLoading = false;
                    return chapters;
                })
            );
    }

    private changeSource(sourceId: string): void {
        // Update active tab UI
        this.activeSource = sourceId;
        this.chapters = [];
        this.chaptersLoaded = false;
        this.updateUIState('not-loaded');
    }

    private renderChapters(chapters: []): void {
        this.chapterList.innerHTML = '';

        if (chapters.length === 0) {
            this.noChaptersElement.classList.remove('d-none');
            this.chapterList.classList.add('d-none');
            return;
        }

        this.noChaptersElement.classList.add('d-none');
        this.chapterList.classList.remove('d-none');

        const fragment = document.createDocumentFragment();

        chapters.forEach(chapter => {
            const clone = this.chapterTemplate.content.cloneNode(true) as DocumentFragment;
            const li = clone.querySelector('li');
            const link = clone.querySelector('a');
            const nameSpan = clone.querySelector('span');
            const dateEm = clone.querySelector('em');

            if (li && link && nameSpan && dateEm) {
                link.href = `/manga/${this.mangaSlug}/chapter/${chapter.chapterId}`;
                link.title = chapter.chapterName;
                nameSpan.textContent = chapter.chapterName;
                dateEm.textContent = "-- " + formatTimeDifference(chapter.lastUpdated);
                fragment.appendChild(clone);
            }
        });

        this.chapterList.appendChild(fragment);
    }

    private filterChapters(searchTerm: string): void {
        if (!this.chaptersLoaded) return;

        const filteredChapters = this.chapters.filter(chapter =>
            chapter.chapterName.toLowerCase().includes(searchTerm.toLowerCase())
        );
        this.renderChapters(filteredChapters);
    }

    private updateUIState(state: 'loading' | 'loaded' | 'error' | 'not-loaded'): void {
        this.chaptersNotLoaded.classList.add('d-none');
        this.loadingElement.classList.add('d-none');
        this.chapterDisplayResults.classList.add('d-none');

        switch (state) {
            case 'loading':
                this.loadingElement.classList.remove('d-none');
                break;
            case 'loaded':
                this.chapterDisplayResults.classList.remove('d-none');
                break;
            case 'not-loaded':
                this.chaptersNotLoaded.classList.remove('d-none');
                break;
            case 'error':
                this.chaptersNotLoaded.classList.remove('d-none');
                break;
        }
    }

    private showToast(message: string, type: 'success' | 'error'): void {
        // Implement according to your toast library
        if (type === 'error') {
            toastr.error(message);
        } else {
            toastr.success(message);
        }
    }
}

// Interfaces
export interface MangaReport {
    id?: string;
    details: string;
    solved?: boolean;
    manga?: string;
    user?: string;
}

export interface MangaOpinion {
    id?: string;
    opinion: boolean | null;
    user?: string;
    mainManga?: string;
}

const OPINION_LIKE = true;
const OPINION_DISLIKE = false;

$(function() {
    new MangaChapterManager();
    // Favorite Action
    $('.favorite_manga').on('click', function(event) {
        const button = $(this);
        const mangaId = button.data('id');

        button.prop('disabled', true);

        if (tokenStorageService.isLoggedIn()) {
            axios.post(`https://api.abyssreader.com/api/users/favorite/${mangaId}`)
                .then(response => {
                    const isFavorite = response.data;
                    if (isFavorite) {
                        button.addClass('on');
                        toastr.success("The manga has been added to your favorites");
                    } else {
                        button.removeClass('on');
                        toastr.success("The manga has been removed from favorites");
                    }
                })
                .catch(error => {
                    toastr.error("An error occurred during the process, please try again!");
                })
                .finally(() => {
                    button.prop('disabled', false);
                });
        } else {
            tokenStorageService.showPoPupLogin();
            button.prop('disabled', false);
        }
    });

    // Planning to Read Action
    $('.planning_manga').on('click', function(event) {
        const button = $(this);
        const mangaId = button.data('id');

        button.prop('disabled', true);

        if (tokenStorageService.isLoggedIn()) {
            axios.post(`https://api.abyssreader.com/api/users/planning-to-read/${mangaId}`)
                .then(response => {
                    const isPlanning = response.data;
                    if (isPlanning) {
                        button.addClass('on');
                        toastr.success("The manga has been added to your planning to read list");
                    } else {
                        button.removeClass('on');
                        toastr.success("The manga has been removed from your planning to read list");
                    }
                })
                .catch(error => {
                    toastr.error("An error occurred during the process, please try again!");
                })
                .finally(() => {
                    button.prop('disabled', false);
                });
        } else {
            tokenStorageService.showPoPupLogin();
            button.prop('disabled', false);
        }
    });

    // Blacklist Action
    $('.blacklist_manga').on('click', function(event) {
        const button = $(this);
        const mangaId = button.data('id');

        button.prop('disabled', true);

        if (tokenStorageService.isLoggedIn()) {
            axios.post(`https://api.abyssreader.com/api/users/blacklist/${mangaId}`)
                .then(response => {
                    const isBlacklisted = response.data;
                    if (isBlacklisted) {
                        button.addClass('on');
                        toastr.success("The manga has been added to your blacklist");
                    } else {
                        button.removeClass('on');
                        toastr.success("The manga has been removed from your blacklist");
                    }
                })
                .catch(error => {
                    toastr.error("An error occurred during the process, please try again!");
                })
                .finally(() => {
                    button.prop('disabled', false);
                });
        } else {
            tokenStorageService.showPoPupLogin();
            button.prop('disabled', false);
        }
    });

    // Like/Dislike Actions
    $('.like_manga, .dislike_manga').on('click', function(event) {
        event.stopPropagation();
        const button = $(this);
        const mangaId = button.data('id');
        const isLike = button.hasClass('like_manga');

        if (tokenStorageService.isLoggedIn()) {
            const currentOpinion = button.data('opinion');
            let opinion = generateOpinion(isLike, currentOpinion, tokenStorageService.getUser().id, mangaId);
            axios.post('https://api.abyssreader.com/api/ratings', opinion)
                .then(response => {
                    const data = response.data;
                    if (data.opinion === null) {
                        button.removeClass('on');
                        button.siblings('.like_manga, .dislike_manga').removeClass('on');
                        toastr.success("Your opinion has been removed from this manga");
                        button.data('opinion', null);
                        button.siblings('.like_manga, .dislike_manga').data('opinion', null);
                    } else {
                        button.addClass('on');
                        button.siblings('.like_manga, .dislike_manga').removeClass('on');
                        toastr.success("Your opinion has been submitted to this manga");
                        button.data('opinion', data);
                        button.siblings('.like_manga, .dislike_manga').data('opinion', data);
                    }
                })
                .catch(error => {
                    toastr.error("An error occurred during the process, please try again!");
                });
        } else {
            tokenStorageService.showPoPupLogin();
        }
    });

    // Report Action
    $('.report_manga').on('click', function(event) {
        const mangaId = $(this).data('id');
        const button = $(this);
        reportPoPup(mangaId, button);
    });
});

function reportPoPup(mangaId: string, button: JQuery<HTMLElement>) {
    jconfirm({
        title: 'Report the manga',
        content:
            '<form class="p-1" id="report_manga" name="report_manga" method="POST" action="">' +
            '<p class="text-danger error_report m-0 mb-2"></p>' +
            '<div class="custom-control custom-checkbox"><input type="checkbox" class="custom-control-input" name="option_report" id="option-report-1" value="Some of the information in the manga is wrong" /><label for="option-report-1" class="custom-control-label">Some of the information in the manga is wrong</label></div>' +
            '<div class="custom-control custom-checkbox"><input type="checkbox" class="custom-control-input" name="option_report" id="option-report-2" value="One of the sources of this manga is uploading the wrong manga" /><label for="option-report-2" class="custom-control-label">One of the sources of this manga is uploading the wrong manga</label></div>' +
            '<div class="custom-control custom-checkbox"><input type="checkbox" class="custom-control-input" name="option_report" id="option-report-3" value="One of the chapters is not working" /><label for="option-report-3" class="custom-control-label">One of the chapters is not working</label></div>' +
            '<div class="custom-control custom-checkbox"><input type="checkbox" class="custom-control-input" name="option_report" id="option-report-4" value="Other problems" /><label for="option-report-4" class="custom-control-label">Other problems</label></div>' +
            '<div class="report">' +
            '<textarea type="text" maxlength="500" id="detail_report" class="md-textarea-auto form-control" placeholder="Explain your problem more precisely or if your problem is not within the options above"></textarea>' +
            '</div>' +
            '</form>',
        type: 'red',
        columnClass: 'medium',
        theme: 'dark',
        buttons: {
            submit: {
                text: "Submit Report",
                btnClass: 'btn-red submit-report',
                action: function(this: any) {
                    const form = this.$content.find('form')[0];
                    const detail_report = form.elements.detail_report.value;
                    let error = this.$content.find(".error_report")[0];

                    let options_report: string[] = [];
                    for (let option of form.elements.option_report) {
                        if (option.checked) {
                            options_report.push(option.value);
                        }
                    }

                    if (options_report.length === 0) {
                        error.textContent = "Please select at least one option";
                        return false;
                    }

                    sendReportManga(detail_report, options_report, this, mangaId, button);
                    return false;
                }
            },
            cancel: {
                text: "Cancel",
                btnClass: 'btn-blue cancel-report'
            }
        }
    });
}

function sendReportManga(detail_report: string, options_report: string[], jc: any, mangaId: string, button: JQuery<HTMLElement>) {
    const report: MangaReport = {
        details: options_report.join(", ") + ".\n" + detail_report,
        manga: mangaId
    };

    button.prop('disabled', true);

    if (tokenStorageService.isLoggedIn()) {
        axios.post('https://api.abyssreader.com/api/manga/report', report)
            .then(response => {
                jc.close();
                toastr.success("We have received your report and will look into it as soon as possible.");
            })
            .catch(error => {
                if (error.response && error.response.status === 409) {
                    jc.setContent("You have already submitted your report on this manga. Please wait until we resolve the issue.");
                } else {
                    jc.setContent("An error occurred while trying to submit your report, please try again.");
                }
            })
            .finally(() => {
                button.prop('disabled', false);
            });
    } else {
        tokenStorageService.showPoPupLogin();
        button.prop('disabled', false);
    }
}

function generateOpinion(status : boolean, currentOpinion: any, userId : string, mangaId : string): MangaOpinion{
    let opinion : MangaOpinion
    if(currentOpinion != null){
      opinion = currentOpinion
      if((status === OPINION_DISLIKE && opinion.opinion === OPINION_DISLIKE) || (status === OPINION_LIKE && opinion.opinion === OPINION_LIKE)){
        opinion.opinion = null
      } else {
        opinion.opinion = status
      }
    } else {
      opinion = {
        opinion : status,
        user : userId,
        mainManga : mangaId
      }
    }
    return opinion
}
